import React, { useCallback, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { storageManagerBottonClick } from '@wix/bi-logger-media-data/v2';
import { IMediaStorageWidget as IWidget, MediaStorageWidget as Widget } from '@wix/media-storage-widget';
import { bi } from '../../../services/loggers/bi';
import { fedopsLogger } from '../../../services/loggers/fedopsLogger';
import { FedOpsInteraction } from '../../../constants/FedOpsInteraction';
import { getLocale } from '../../../utils/env/getLocale';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useStorageQuota } from '../../../utils/hooks/storageQuota/useStorageQuota';
import { calculateBiStatus, useStorageQuotaBI } from '../../../utils/hooks/storageQuota/useStorageQuotaBI';
import { parseSiteMediaToken } from '../../../utils/siteMediaToken';
import styles from './MediaStorageWidget.scss';

export const MediaStorageWidget = () => {
  const { storageQuotaData, isLoading, isStorageQuotaVisibilityEnabled, isStorageQuotaGracePeriodEnabled } =
    useStorageQuota();
  const { trackUpgradeButtonClick } = useStorageQuotaBI(storageQuotaData);
  const wasLoading = usePrevious(isLoading);

  const {
    mmgrSettings: { siteMediaToken, isManageStorageButtonEnabled, hideMediaStorageWidget },
  } = useMMGRSettings();
  const { metaSiteId } = parseSiteMediaToken(siteMediaToken);

  useEffect(() => {
    if (isLoading && !wasLoading) {
      fedopsLogger.interactionStarted(FedOpsInteraction.StorageQuotaDataLoad);
    } else if (!isLoading && wasLoading && storageQuotaData) {
      fedopsLogger.interactionEnded(FedOpsInteraction.StorageQuotaDataLoad);
    }
  }, [isLoading, wasLoading, storageQuotaData]);

  const handleUpgradePlanButtonClick = useCallback<IWidget['onUpgradeButtonClick']>(
    ({ fromTooltip }) => {
      trackUpgradeButtonClick({ upgradeOrigin: fromTooltip ? 'tooltip' : 'storage_indicator' });
    },
    [trackUpgradeButtonClick],
  );

  const handleStorageManagerButtonClick = () => {
    const storageStatus = calculateBiStatus(storageQuotaData);
    bi.logger().report(
      storageManagerBottonClick({
        buttonOrigin: 'storage widget',
        storage_status: storageStatus,
      }),
    );
  };

  return (
    !hideMediaStorageWidget &&
    isStorageQuotaVisibilityEnabled && (
      <div className={styles.wrapper}>
        <Widget
          locale={getLocale()}
          isLoading={isLoading}
          dataHook="media-storage-widget"
          quota={storageQuotaData?.quota.storage ?? 0}
          usage={storageQuotaData?.usage.storage ?? 0}
          upgradeUrl={storageQuotaData?.upgradeUrl}
          manageStorageUrl={
            isManageStorageButtonEnabled
              ? `https://manage.wix.com/dashboard/${metaSiteId}/storage-manager?referralInfo=media-manager`
              : undefined
          }
          gracePeriodDaysLeft={isStorageQuotaGracePeriodEnabled ? storageQuotaData?.gracePeriodDaysLeft : undefined}
          onStorageManagerClick={handleStorageManagerButtonClick}
          onUpgradeButtonClick={handleUpgradePlanButtonClick}
        />
      </div>
    )
  );
};
